import { useEffect, useRef, useState } from 'react'
import type { ShowcaseScreen, ShowcaseVariant } from './fixtures'

// A framed, scaled window onto the REAL dashboard (plan 2026-10-18).
//
// The dashboard is its own document (/features/showcase/:screen, the
// `showcase` entrypoint) in a same-origin iframe laid out 1280px wide, so every
// portal media query reads 1280 whatever the visitor's screen, and the portal's
// stylesheets apply exactly as they do in the dashboard without touching this
// page. The iframe is then scaled down to the frame's width.
//
// Progressive enhancement. The server (and the browser before the frame nears
// the viewport) draws a placeholder of the same size carrying the label, so the
// page copy never waits on it and the layout never jumps.
//
// It is a picture, not an app: the wrapper is role="img" with a label, the
// iframe is aria-hidden, out of the tab order and takes no pointer events (the
// page scrolls straight past it), and its own document is inert and swallows
// clicks and submits.
//
// The exceptions are the live-chat and lyrics demos (`live-chat-full`,
// `lyrics-full`): there the chat or the studio is meant to be used, so the
// frame is a named, focusable iframe that takes the pointer, and its document
// makes only that demo live (the dashboard under it stays inert). The page around it must say it is a demo.

/** The width the dashboard is laid out at before it is scaled down to fit. */
export const STAGE_WIDTH = 1280

/** Screens whose frame can be used, not just looked at. */
export const INTERACTIVE_SCREENS = ['live-chat-full', 'lyrics-full'] as const satisfies readonly ShowcaseScreen[]
export type InteractiveScreen = (typeof INTERACTIVE_SCREENS)[number]

/** How tall an interactive demo's frame is: room for the whole chat panel or studio dialog. */
export const INTERACTIVE_FRAME_HEIGHT = 920

export function isInteractive(screen: ShowcaseScreen): screen is InteractiveScreen {
  return (INTERACTIVE_SCREENS as readonly ShowcaseScreen[]).includes(screen)
}

export function showcaseSrc(screen: ShowcaseScreen, variant: ShowcaseVariant) {
  return `/features/showcase/${screen}?variant=${variant}`
}

interface ShowcaseProps {
  screen: ShowcaseScreen
  variant: ShowcaseVariant
  /** What the frame shows, for screen readers and the placeholder. */
  label: string
  /** The dashboard's height in its own 1280px-wide pixels. */
  height?: number
  /** The address shown in the browser chrome. */
  address?: string
  className?: string
}

export default function Showcase({ screen, variant, label, height = 800, address = 'wavbee.com/dashboard', className = '' }: ShowcaseProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [near, setNear] = useState(false)
  const [scale, setScale] = useState<number | null>(null)
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
  const src = showcaseSrc(screen, variant)
  const interactive = isInteractive(screen)

  // Load the frame once it is near the viewport (and only in a browser).
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setNear(true); return }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setNear(true); io.disconnect() }
    }, { rootMargin: '400px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // Scale the 1280px document to the frame's width; the viewport keeps its
  // aspect ratio, so the scaled iframe fills it with no gap below.
  useEffect(() => {
    const el = viewportRef.current
    if (!el || !near) return
    const measure = () => { if (el.clientWidth > 0) setScale(el.clientWidth / STAGE_WIDTH) }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [near])

  return (
    <figure className={`mx-auto w-full max-w-6xl ${className}`}>
      <div
        role={interactive ? undefined : 'img'}
        aria-label={interactive ? undefined : label}
        className="relative overflow-hidden rounded-xl border border-white/10 bg-[#050506] shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
      >
        <div aria-hidden="true" className="flex items-center gap-3 border-b border-white/10 bg-[#0a0a0a] px-3 py-2">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
          </div>
          <div className="mx-auto rounded-full border border-white/10 bg-[#050506] px-4 py-0.5 text-[10px] text-white/60">{address}</div>
        </div>

        <div
          ref={viewportRef}
          data-showcase-viewport=""
          className="relative w-full overflow-hidden"
          style={{ aspectRatio: `${STAGE_WIDTH} / ${height}` }}
        >
          {loadedSrc !== src && (
            <div data-testid="showcase-placeholder" className="absolute inset-0 flex items-center justify-center p-6">
              <p className="text-center text-sm text-white/50">{label}</p>
            </div>
          )}
          {near && (
            <iframe
              // A new element per variant: changing an iframe's src would add an
              // entry to this page's history, so Back would step the frame.
              key={src}
              src={src}
              title={label}
              aria-hidden={interactive ? undefined : 'true'}
              tabIndex={interactive ? undefined : -1}
              loading="lazy"
              scrolling="no"
              width={STAGE_WIDTH}
              height={height}
              onLoad={() => setLoadedSrc(src)}
              className={`${interactive ? '' : 'pointer-events-none '}absolute left-0 top-0 border-0`}
              style={{
                width: STAGE_WIDTH,
                height,
                transform: `scale(${scale ?? 1})`,
                transformOrigin: '0 0',
                visibility: scale == null ? 'hidden' : undefined,
              }}
            />
          )}
        </div>
      </div>
      <figcaption className="mt-3 text-center text-xs text-white/50">Sample data</figcaption>
    </figure>
  )
}
